<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import DateRangePicker from '@/components/ui/DateRangePicker.vue';
import TripDurationSelector from '@/components/ui/TripDurationSelector.vue';
import HolidaySelector from '@/components/shared/HolidaySelector.vue';
import type { CalendarRestriction, DateRange, DurationOption } from '@/types/SearchBar.ts';
import { useFilterConfigurationStore } from '@/stores/filterConfiguration.ts';
import { nightsInRange, type DurationMode } from '@/utils/filters/durationMode.ts';
import { $t } from '@/composables/translations/useTranslate.ts';
import { useAnalytics } from '@/composables/useAnalytics.ts';

interface Props {
  modelValue?: DateRange;
  duration?: number;
  durationMode?: DurationMode;
  minDate?: Date | string | null;
  maxDate?: Date | string | null;
  locale?: string;
  availableDurations?: DurationOption[];
  adHocDuration?: number | null;
  restriction?: CalendarRestriction;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  duration: 7,
  durationMode: 'preset',
  adHocDuration: null,
  minDate: () => new Date(),
  maxDate: () => {
    const nextYear = new Date();
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    return nextYear;
  },
  locale: 'de',
  availableDurations: () => [],
  restriction: undefined,
});

const filterConfigurationStore = useFilterConfigurationStore();
const analytics = useAnalytics();

const emit = defineEmits<{
  'update:modelValue': [dateRange: DateRange];
  'update:duration': [duration: number];
  'update:durationMode': [mode: DurationMode];
  'pending-change': [pending: boolean];
}>();

const localDateRange = ref<DateRange>(props.modelValue);
const localDuration = ref<number>(props.duration);
const durationMode = ref<DurationMode>(props.durationMode);
const selectedHolidayKey = ref<string | null>(null);
const localAdHoc = ref<number | null>(props.adHocDuration);
const dateRangePickerRef = ref<InstanceType<typeof DateRangePicker> | null>(null);
let suppressHolidayResetOnRangeChange = false;

/**
 * Effective duration options - either from props or from store
 * Props take precedence (allows override for testing/special cases)
 */
const effectiveDurations = computed<DurationOption[]>(() => {
  const baseOptions =
    props.availableDurations && props.availableDurations.length > 0
      ? props.availableDurations
      : filterConfigurationStore.durationOptions;

  const adHoc = localAdHoc.value;
  const options =
    adHoc && adHoc > 0 && !baseOptions.some((o) => o.type === 'preset' && o.value === adHoc)
      ? [
          ...baseOptions,
          {
            value: adHoc,
            label: $t('ibe.duration.n-days', '{n} days', { n: adHoc }),
            type: 'preset' as const,
          },
        ]
      : baseOptions;

  return [...options].sort((a, b) => a.value - b.value);
});

/**
 * Presets the hotel cannot serve are hidden. The selected one stays visible
 * with its own label even then, so the user still sees what is set.
 */
const visibleDurations = computed<DurationOption[]>(() => {
  const restriction = props.restriction;
  if (!restriction || restriction.durationsStatus !== 'ready') return effectiveDurations.value;

  return effectiveDurations.value.filter(
    (option) =>
      option.type === 'exact' ||
      restriction.isPresetOffered(option.value) ||
      (durationMode.value === 'preset' && option.value === localDuration.value)
  );
});

const durationsLoading = computed(() => props.restriction?.durationsStatus === 'loading');

/**
 * Auto-calculated duration from date range
 */
const calculatedDuration = computed(() => nightsInRange(localDateRange.value));

/**
 * Hint between the duration pills and the calendar: Exact picks the trip
 * itself, a preset picks the window the trip has to fit in.
 */
const durationHint = computed(() =>
  durationMode.value === 'exact'
    ? $t('ibe.duration.hint-exact', 'Select your exact travel dates:')
    : $t('ibe.duration.hint-preset', 'Select earliest arrival – latest departure:')
);

function applyDateRange(dateRange: DateRange) {
  localDateRange.value = dateRange;
  emit('update:modelValue', dateRange);

  // If in exact mode, update duration automatically
  if (durationMode.value === 'exact') {
    const newDuration = calculatedDuration.value;
    localDuration.value = newDuration;
    emit('update:duration', newDuration);
  } else if (
    durationMode.value === 'preset' &&
    calculatedDuration.value > 0 &&
    localDuration.value > calculatedDuration.value
  ) {
    const fallback =
      (props.restriction?.durationsStatus === 'ready'
        ? props.restriction.fallbackDuration(calculatedDuration.value)
        : null) ??
      Math.max(1, Math.min(filterConfigurationStore.defaultDuration, calculatedDuration.value));
    localDuration.value = fallback;
    localAdHoc.value = fallback;
    emit('update:duration', fallback);
  }
}

/**
 * Handler: Date range changed (calendar or programmatic)
 */
function handleDateRangeChange(dateRange: DateRange) {
  if (!suppressHolidayResetOnRangeChange) {
    selectedHolidayKey.value = null;
  }
  applyDateRange(dateRange);
}

function handleHolidaySelect(range: DateRange) {
  if (!range) return;
  suppressHolidayResetOnRangeChange = true;
  try {
    applyDateRange(range);
    analytics.trackHolidaySelected(range);
  } finally {
    suppressHolidayResetOnRangeChange = false;
  }
  void dateRangePickerRef.value?.focusDate(range[0]);
}

/**
 * Handler: Duration option selected
 */
function handleDurationSelect(option: DurationOption) {
  if (option.type === 'exact') {
    durationMode.value = 'exact';
    const newDuration = calculatedDuration.value;
    localDuration.value = newDuration;
    emit('update:duration', newDuration);
    emit('update:durationMode', 'exact');
  } else {
    durationMode.value = 'preset';
    localDuration.value = option.value;
    emit('update:duration', option.value);
    emit('update:durationMode', 'preset');
  }
}

watch(
  () => props.modelValue,
  (newValue) => {
    localDateRange.value = newValue;
  }
);

watch(
  () => props.duration,
  (newValue) => {
    localDuration.value = newValue;
  }
);

watch(
  () => props.durationMode,
  (newValue) => {
    durationMode.value = newValue;
  }
);

watch(
  () => props.adHocDuration,
  (newValue) => {
    localAdHoc.value = newValue;
  }
);
</script>

<template>
  <div class="date-range-modal-content flex flex-col gap-4">
    <TripDurationSelector
      v-model="localDuration"
      :class="{ 'animate-pulse': durationsLoading }"
      :options="visibleDurations"
      :mode="durationMode"
      :max-duration="calculatedDuration"
      :disabled="durationsLoading"
      @select="handleDurationSelect"
    />
    <p class="duration-hint text-center text-sm text-text-secondary">
      {{ durationHint }}
    </p>
    <DateRangePicker
      ref="dateRangePickerRef"
      v-model="localDateRange"
      :min-date="minDate"
      :max-date="maxDate"
      :locale="locale"
      :restriction="restriction"
      :duration-mode="durationMode"
      @update:model-value="handleDateRangeChange"
      @pending-change="emit('pending-change', $event)"
    />
    <div class="w-full h-px bg-gray-300"></div>
    <HolidaySelector
      v-model="selectedHolidayKey"
      :min-date="minDate"
      :max-date="maxDate"
      @select="handleHolidaySelect"
    />
  </div>
</template>
